import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import '../../css/NumberPicker.css';

export default function NumberPicker({
  isOpen,
  onClose,
  onSelect,
  currentValue = 1,
  position = { top: 200, left: 200 },
  blockType = null,
}) {
  const isOpacityPicker = blockType === 'video_transparency';
  const isCreativityPicker = blockType === 'aiCreativity';
  const minValue = (isOpacityPicker || isCreativityPicker) ? 0 : 1;
  const maxValue = isCreativityPicker ? 1 : (isOpacityPicker ? 100 : Number.MAX_SAFE_INTEGER);
  const [display, setDisplay] = useState(String(minValue));

  const clampValue = (raw) => {
    const parsed = isCreativityPicker ? parseFloat(raw) : parseInt(raw, 10);
    if (Number.isNaN(parsed)) return minValue;
    return Math.min(maxValue, Math.max(minValue, parsed));
  };

  useEffect(() => {
    if (isOpen) {
      setDisplay(String(clampValue(currentValue)));
    }
  }, [isOpen, currentValue, minValue, maxValue]);

  if (!isOpen) return null;

  const pushDigit = (d) => {
    setDisplay((prev) => {
      const base = prev === '0' ? '' : prev;
      const next = `${base}${d}`;
      if (!isCreativityPicker) {
        return String(clampValue(next));
      }
      let normalized = next;
      if (normalized === '.') normalized = '0.';
      if (normalized.startsWith('.')) normalized = `0${normalized}`;
      const dotIndex = normalized.indexOf('.');
      if (dotIndex !== -1) {
        normalized = normalized.slice(0, dotIndex + 1) + normalized.slice(dotIndex + 1).replace(/\./g, '');
        const parts = normalized.split('.');
        const head = parts[0] || '0';
        const tail = parts[1] || '';
        normalized = tail ? `${head}.${tail.slice(0, 2)}` : `${head}.`;
      }
      const numeric = parseFloat(normalized);
      if (Number.isFinite(numeric)) {
        const clamped = clampValue(numeric);
        if (numeric !== clamped) {
          normalized = String(clamped);
        }
      }
      return normalized || '0';
    });
  };

  const back = () => {
    setDisplay((prev) => {
      const trimmed = prev.slice(0, -1);
      if (!trimmed.length) return String(minValue);
      if (isCreativityPicker && trimmed === '.') return '0.';
      return String(clampValue(trimmed));
    });
  };

  const clear = () => setDisplay(String(minValue));

  const ok = () => {
    const n = clampValue(display);
    onSelect(n);
    onClose();
  };

  // ---- PORTAL CONTENT ----
  const picker = (
    <div
      className="np-portal-wrapper"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        zIndex: 999999,
        pointerEvents: 'auto'
      }}
    >
      <div className="np-overlay" onMouseDown={onClose}>
        <div className="np-box" onMouseDown={(e) => e.stopPropagation()}>
          <div className="np-title">{isCreativityPicker ? 'Creativity (0.0-1.0)' : 'How many times?'}</div>

          <div className="np-screen"><span>{display}</span></div>

          <div className="np-grid">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} className="np-btn" onClick={() => pushDigit(n)}>{n}</button>
            ))}
            <button className="np-btn np-clear" onClick={clear}>Clear</button>
            <button className="np-btn" onClick={() => pushDigit(0)}>0</button>
            {isCreativityPicker
              ? <button className="np-btn" onClick={() => pushDigit('.')}>.</button>
              : null}
            <button className="np-btn np-back" onClick={back}>Back</button>
          </div>

          <div className="np-actions">
            <button className="np-cancel" onClick={onClose}>Cancel</button>
            <button className="np-ok" onClick={ok}>OK</button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(picker, document.body);
}
